import { FC } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { ProjectDataType } from "data/types";
import SafeImage from "components/SafeImage/SafeImage";
import { uniqueExcerpt } from "utils/imageUtils";

export interface ProjectCardProps {
  project: ProjectDataType;
  className?: string;
}

const ProjectCard: FC<ProjectCardProps> = ({ project, className = "" }) => {
  const { id, name, description, images, date } = project;
  const excerpt = uniqueExcerpt(name, description, 70);

  return (
    <article
      className={`group relative flex flex-col rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:shadow-bcr-card hover:border-primary-300/50 dark:hover:border-primary-600/40 transition-all duration-300 ${className}`}
    >
      <Link to={`/projects/${id}`} className="flex flex-col h-full">
        <div className="relative aspect-[4/3] max-h-[160px] overflow-hidden bg-neutral-100 dark:bg-neutral-800">
          <SafeImage
            src={images}
            alt={name}
            placeholderVariant="project"
            placeholderCompact
            objectFit="cover"
            imgClassName="group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>

        <div className="flex flex-col flex-1 p-3 sm:p-4">
          <div className="flex items-start justify-between gap-2">
            <h2
              className="font-semibold text-sm sm:text-base text-neutral-900 dark:text-white leading-snug line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
              title={name}
            >
              {name}
            </h2>
          </div>

          {date && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-500 dark:text-neutral-400">
              <CalendarIcon className="w-3 h-3 shrink-0" />
              {date}
            </p>
          )}

          {excerpt && (
            <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-2">
              {excerpt}
            </p>
          )}

          <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
              View project
            </span>
            <ArrowRightIcon className="w-4 h-4 text-primary-500 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ProjectCard;
